import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faLayerGroup } from '@fortawesome/pro-light-svg-icons/faLayerGroup';
import { faPalette } from '@fortawesome/pro-light-svg-icons/faPalette';
import { faGear } from '@fortawesome/pro-light-svg-icons/faGear';
import { TypedMemo } from '../../../common/types';
import { editorMessages } from '../editorMessages';
import { EditorTab } from '../url/useEditorUrlState';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLEditorPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TabButtonProps {
  tab: EditorTab;
  label: string;
  icon: IconDefinition;
  count?: number;
  isOn: boolean;
  onSelect: (tab: EditorTab) => void;
}

const TabButtonComponent: React.FC<TabButtonProps> = ({ tab, label, icon, count, isOn, onSelect }) => {
  const select = useCallback(() => onSelect(tab), [onSelect, tab]);
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isOn}
      className={clsx(styles.tab, isOn && styles.tabOn)}
      onClick={select}
      data-testid={`editor-tab-${tab}`}
    >
      <FontAwesomeIcon icon={icon} />
      {label}
      {count !== undefined && <span className={styles.count}>{count}</span>}
    </button>
  );
};

const TabButton = TypedMemo(TabButtonComponent);

interface SMLEditorTabsProps {
  tab: EditorTab;
  blockCount: number;
  onSelect: (tab: EditorTab) => void;
}

/** The three tabs of the panel (design a1): Blocks (with the count), Look and Page. */
const SMLEditorTabsComponent: React.FC<SMLEditorTabsProps> = ({ tab, blockCount, onSelect }) => {
  const intl = useIntl();
  return (
    <div className={styles.tabs} role="tablist" aria-label={intl.formatMessage(editorMessages.tabsLabel)}>
      <TabButton
        tab="blocks"
        label={intl.formatMessage(editorMessages.tabBlocks)}
        icon={faLayerGroup}
        count={blockCount}
        isOn={tab === 'blocks'}
        onSelect={onSelect}
      />
      <TabButton
        tab="look"
        label={intl.formatMessage(editorMessages.tabLook)}
        icon={faPalette}
        isOn={tab === 'look'}
        onSelect={onSelect}
      />
      <TabButton
        tab="page"
        label={intl.formatMessage(editorMessages.tabPage)}
        icon={faGear}
        isOn={tab === 'page'}
        onSelect={onSelect}
      />
    </div>
  );
};

export const SMLEditorTabs = TypedMemo(SMLEditorTabsComponent);
